import { useMemo } from 'react'

import clsx from 'clsx'
import { displayFullNumericalDate, displayTime } from 'lib/utilities/dateTime'
import { asCurrency } from 'lib/utilities/number'
import Button, { ButtonProps } from '../Button/Button'
import DeliverySteps from '../DeliverySteps/DeliverySteps'
import Header from '../Header/Header'
import RouteDates from './partials/RouteDates'

export interface AccountOrder {
  createdAt: string
  id: string
  legs: Leg[]
  state: string
  totalPriceCents: number
  user: {
    firstName: string
    lastName: string
  }
}

interface Leg {
  pickup: boolean
  state: string
  shipRoute: ShipRoute
  shipments: {
    arrivalDate: string
    product?: {
      productLine: {
        name: string
      }
      sku: string
    }
    shipDate: string
    trackingId?: string
  }[]
}

interface ShipRoute {
  origin: ShipPoint
  destination: ShipPoint
}

interface ShipPoint {
  address1: string
  attentionName?: string
  city: string
  companyName?: string
  countryCode: string
  facilityId?: string
  state: string
  zip5: string
}

export interface OrderCardProps {
  /**
   * The order to display.
   */
  order: AccountOrder
  /**
   * The button details for viewing the order details.
   */
  viewDetails: ButtonProps
  /**
   * Optional button details for booking the order again.
   */
  bookAgain?: ButtonProps
  /**
   * Optional origin point to display.
   */
  origin?: ShipPoint
  /**
   * Optional destination point to display.
   */
  destination?: ShipPoint
  /**
   * Optional leg to display route dates for.
   */
  leg?: Leg
}

export default function OrderCard({
  order,
  viewDetails,
  bookAgain,
  origin,
  destination,
  leg,
}: OrderCardProps) {
  const shipmentStatus = useMemo(() => {
    const delivered = leg?.state.toLowerCase() === 'delivered'
    const shipped = leg?.state.toLowerCase() === 'shipped'

    if (delivered) {
      return 'Delivered'
    } else if (shipped) {
      return 'Shipped'
    }

    return 'Order created'
  }, [leg?.state])

  return (
    <article
      className={clsx(
        'border rounded-8 mt-4 p-4 lg:mt-6 lg:px-6 lg:py-4',
        order.state === 'cancelled' || order.state === 'complete'
          ? 'bg-neutral-200 border-neutral-200'
          : 'border-neutral-400',
      )}
    >
      <div className="lg:flex lg:justify-between lg:items-center lg:gap-x-4">
        <div className="type-body-8 lg:type-body-10 lg:grid lg:gap-2 lg:flex-1 lg:max-w-[616px] lg:grid-cols-[1fr_auto] lg:items-center lg:grid-flow-dense">
          <Header as="h3" className="[&]:text-base [&]:leading-[1.3]">
            Order ID: {`${order.id} `}
            <span
              className={clsx(
                'border-l border-neutral-400 ml-[5px] pl-2 capitalize',
                order.state === 'active' && 'text-theme-accessibility',
                order.state === 'cancelled' && 'text-error',
              )}
            >
              {order.state}
            </span>
          </Header>
          <div className="lg:text-right lg:row-span-2 lg:self-start flex flex-col gap-2">
            <p className="font-bold lg:col-start-2">
              {`${order.user.lastName}, ${order.user.firstName}`}
            </p>
            <div className="w-full lg:order-1">
              {asCurrency(order.totalPriceCents / 100)}
            </div>
            <div className="lg:contents">
              <span className="lg:col-start-2 lg:row-start-2">
                <span className="mr-2 lg:col-start-2 lg:text-right lg:mr-0">
                  {displayFullNumericalDate(order.createdAt)}
                  <span className="hidden lg:inline">, </span>
                </span>
                <span>{displayTime(order.createdAt)}</span>
              </span>
              <span className="ml-2 lg:order-2">
                {leg?.shipments?.length ?? 0} items
              </span>
            </div>
          </div>
          <div className="mt-2 lg:mt-0">
            <div>
              <p className="w-full">
                <strong>Origin:</strong> {origin?.address1}
                {'. '}
                {origin?.city}
                {', '}
                {origin?.state} {origin?.zip5}
              </p>
              <p className="w-full">
                <strong>Destination:</strong> {destination?.address1}
                {'. '}
                {destination?.city}
                {', '}
                {destination?.state} {destination?.zip5}
              </p>
            </div>
            <div className="mt-2 lg:mt-4 lg:flex lg:items-center lg:gap-6">
              <RouteDates
                routeNumber={1}
                pickup={leg?.pickup}
                pickupDate={leg?.shipments?.[0]?.shipDate || ''}
                deliveryDate={leg?.shipments?.[0]?.arrivalDate || ''}
              />
            </div>
          </div>
          <div className="w-full mt-6 lg:mt-4 lg:col-span-2">
            {order.state.toLowerCase() === 'cancelled' ? (
              <DeliverySteps
                steps={['Order created', 'Order cancelled']}
                currentStep="Order cancelled"
                isCancelled={true}
              />
            ) : (
              <DeliverySteps
                steps={['Order created', 'Shipped', 'Delivered']}
                currentStep={shipmentStatus}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-6 lg:mt-0">
          {leg?.shipments?.[0]?.trackingId && (
            <Button
              onClick={viewDetails.onClick}
              className="[&]:text-xs w-full [&]:min-h-0 min-w-[120px] p-4 lg:w-auto lg:mt-0"
            >
              View details
            </Button>
          )}
          {(order.state.toLowerCase() === 'complete' ||
            order.state.toLowerCase() === 'cancelled') &&
            bookAgain && (
              <Button
                onClick={bookAgain.onClick}
                variant="secondary"
                className="[&]:text-xs w-full [&]:min-h-0 min-w-[120px] p-4 lg:w-auto lg:mt-0"
              >
                Book again
              </Button>
            )}
        </div>
      </div>
    </article>
  )
}
